import { useState, useEffect, useRef } from "react";
import { Button, Modal, Row, Col, Form } from "react-bootstrap";
import { useSelector } from "react-redux";

const AdminCreateQuotePageComponent = ({
  getUsers,
  getAdminDeliveryBooks,
  adminCreateQuote,
  refreshQuotes,
  uploadImagesApiRequest,
  uploadImagesBunnyApiRequest,
}) => {
  const emptyQuoteItem = {
    productName: "",
    userDescription: "",
    quantity: 1,
    quoteType: "Exact_Product_Required",
    images: [],
  };

  const [show, setShow] = useState(false);
  const [users, setUsers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [quoteItems, setQuoteItems] = useState([{ ...emptyQuoteItem }]);
  const [isCreating, setIsCreating] = useState("");
  const [validated, setValidated] = useState(false);

  const [createQuoteResponseState, setCreateQuoteResponseState] = useState({
    message: "",
    error: "",
  });

  const userInfo = useSelector((state) => state.userRegisterLogin.userInfo);
  const formRef = useRef(null);

  const resetForm = () => {
    setUploading(false);
    setShow(false);
    setSelectedCompany("");
    setSelectedUser(null);
    setUserSearch("");
    setQuoteItems([{ ...emptyQuoteItem }]);
    setIsCreating("");
    setValidated(false);
    setCreateQuoteResponseState({
      message: "",
      error: "",
    });

    if (formRef.current) {
      formRef.current.reset();
    }
  };

  const handleClose = () => {
    resetForm();
  };

  const handleShow = () => setShow(true);

  useEffect(() => {
    getAdminDeliveryBooks()
      .then((adminDeliveryBooks) => {
        const companies = new Set(
          adminDeliveryBooks
            .map((book) => book.companyName)
            .filter((company) => company)
        );

        setCompanyList([...companies].sort());
      })
      .catch((err) =>
        console.log(err.response?.data?.message || err.response?.data || err)
      );
  }, [getAdminDeliveryBooks]);

  useEffect(() => {
    if (show) {
      getUsers().then((fetchedUsers) => {
        setUsers(fetchedUsers);
      });
    }
  }, [show, getUsers]);

  useEffect(() => {
    const filtered = users.filter(
      (user) =>
        (selectedCompany ? user.company === selectedCompany : true) &&
        (user.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
          user.lastName?.toLowerCase().includes(userSearch.toLowerCase()) ||
          user.email?.toLowerCase().includes(userSearch.toLowerCase()))
    );

    setFilteredUsers(filtered);

    if (filtered.length === 1) {
      setSelectedUser(filtered[0]);
    }
  }, [selectedCompany, userSearch, users]);

  const handleCompanyChange = (e) => {
    setSelectedCompany(e.target.value);
    setSelectedUser(null);
  };

  const handleUserSearchChange = (e) => {
    setUserSearch(e.target.value);
  };

  const handleUserSelection = (e) => {
    const userEmail = e.target.value;
    const user = users.find((user) => user.email === userEmail);
    setSelectedUser(user || null);
  };

  const handleQuoteItemChange = (index, field, value) => {
    const updatedItems = [...quoteItems];

    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };

    setQuoteItems(updatedItems);
  };

  const addQuoteItem = () => {
    setQuoteItems([...quoteItems, { ...emptyQuoteItem }]);
  };

  const removeQuoteItem = (index) => {
    if (quoteItems.length === 1) return;

    const updatedItems = quoteItems.filter((_, i) => i !== index);
    setQuoteItems(updatedItems);
  };


  const validateQuoteItems = () => {
    return quoteItems.every(
      (item) =>
        item.productName.trim() &&
        item.userDescription.trim() &&
        item.quantity &&
        Number(item.quantity) > 0 &&
        item.quoteType
    );
  };

  const handleQuoteItemImages = (index, files) => {
    const updatedItems = [...quoteItems];

    updatedItems[index] = {
      ...updatedItems[index],
      images: Array.from(files),
    };

    setQuoteItems(updatedItems);
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();

    setValidated(true);
    setCreateQuoteResponseState({
      message: "",
      error: "",
    });

    if (!selectedCompany) {
      setCreateQuoteResponseState({
        message: "",
        error: "Please select a Company.",
      });
      return;
    }
    if (!selectedUser) {
      setCreateQuoteResponseState({
        message: "",
        error: "Please select a user.",
      });
      return;
    }

    if (!validateQuoteItems()) {
      setCreateQuoteResponseState({
        message: "",
        error: "Please complete all product details.",
      });
      return;
    }

    try {
      setUploading(true);

      const quoteDetails = {
        userId: selectedUser._id,
        userName: `${selectedUser.name} ${selectedUser.lastName}`,
        userEmail: selectedUser.email,

        name:
          quoteItems.length > 1
            ? "Multiple Items"
            : quoteItems[0].productName,

        userDescription: quoteItems[0].userDescription,
        quoteType: quoteItems[0].quoteType,
        quantity: Number(quoteItems[0].quantity) || 1,
        createdBy: userInfo?.name,

        items: quoteItems.map((item) => ({
          productName: item.productName,
          userDescription: item.userDescription,
          quoteType: item.quoteType,
          quantity: Number(item.quantity) || 1,
          existingProduct: false,
          clientImages: item.images || [],
          unitPrice: 0,
          purchasePrice: 0,
          totalPrice: 0,
        })),
      };

      const quote = await adminCreateQuote(quoteDetails);

      for (const item of quoteItems) {
        if (item.images?.length > 0) {
          if (process.env.NODE_ENV === "dev") {
            await uploadImagesApiRequest(
              item.images,
              quote.quoteId
            );
          } else {
            await uploadImagesBunnyApiRequest(
              item.images,
              quote.quoteId
            );
          }
        }
      }

      setCreateQuoteResponseState({
        message: "Quote submitted successfully.",
        error: "",
      });

      refreshQuotes();

      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.log(err.response?.data?.message || err.response?.data || err);

      setCreateQuoteResponseState({
        message: "",
        error:
          err.response?.data?.message ||
          err.response?.data ||
          "An error occurred while creating the quote.",
      });

      setUploading(false);
    }
  };

  return (
    <>
      <Button variant="success" className="ms-2" onClick={handleShow}>
        Create Quote
      </Button>

      <Modal
        show={show}
        onHide={handleClose}
        className="product_quotes_items"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Quote</Modal.Title>
        </Modal.Header>

        <Modal.Body className="mb-2 pb-0">
          <Form
            noValidate
            validated={validated}
            ref={formRef}
            onSubmit={handleCreateQuote}
          >
            <Row>
              <Col md={6}>
                <Form.Label>Company Name:</Form.Label>
                <Form.Select
                  onChange={handleCompanyChange}
                  value={selectedCompany}
                  className="p-1 ps-2"
                >
                  <option value="">Select Company</option>
                  {companyList.map((company, idx) => (
                    <option key={idx} value={company}>
                      {company}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6}>
                <Form.Label>User Search:</Form.Label>
                <Form.Control
                  className="p-1 ps-2"
                  type="text"
                  placeholder="Search User by Name or Email"
                  value={userSearch}
                  onChange={handleUserSearchChange}
                />
              </Col>
            </Row>

            <Row className="mt-2">
              <Col md={12}>
                <Form.Label>User:</Form.Label>
                <Form.Select
                  required
                  onChange={handleUserSelection}
                  value={selectedUser ? selectedUser.email : ""}
                  className="p-1 ps-2"
                >
                  <option value="">Select a user</option>
                  {filteredUsers.map((user, idx) => (
                    <option key={idx} value={user.email}>
                      {`${user.name} ${user.lastName} (${user.email})`}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col md={12}>
                <Form.Label>Selected User:</Form.Label>
                <p className="mb-1">
                  {selectedUser
                    ? `${selectedUser.name} ${selectedUser.lastName} (${selectedUser.email})`
                    : "Not Selected"}
                </p>
              </Col>
            </Row>

            <hr />

            <h5>Quote Products</h5>

            <div className="table-responsive">
              <table className="table table-sm table-bordered align-middle">
                <thead>
                  <tr>
                    <th style={{ width: "2%" }}>#</th>
                    <th style={{ width: "25%" }}>Product Name</th>
                    <th style={{ width: "25%" }}>Description</th>
                    <th style={{ width: "8%" }}>Qty</th>
                    <th style={{ width: "19%" }}>Quote Type</th>
                    <th style={{ width: "12%" }}>Images</th>
                    <th style={{ width: "5%" }}></th>
                  </tr>
                </thead>

                <tbody>
                  {quoteItems.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>

                      <td>
                        <Form.Control
                          required
                          size="sm"
                          type="text"
                          placeholder="Product name"
                          value={item.productName}
                          onChange={(e) =>
                            handleQuoteItemChange(
                              index,
                              "productName",
                              e.target.value
                            )
                          }
                        />
                      </td>

                      <td>
                        <Form.Control
                          required
                          size="sm"
                          as="textarea"
                          rows={1}
                          placeholder="Description"
                          value={item.userDescription}
                          onChange={(e) =>
                            handleQuoteItemChange(
                              index,
                              "userDescription",
                              e.target.value
                            )
                          }
                        />
                      </td>

                      <td>
                        <Form.Control
                          required
                          size="sm"
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuoteItemChange(
                              index,
                              "quantity",
                              e.target.value
                            )
                          }
                        />
                      </td>

                      <td>
                        <Form.Select
                          required
                          size="sm"
                          value={item.quoteType}
                          onChange={(e) =>
                            handleQuoteItemChange(
                              index,
                              "quoteType",
                              e.target.value
                            )
                          }
                        >
                          <option value="Exact_Product_Required">
                            Exact Product Required
                          </option>
                          <option value="Alternative_Accepted">
                            Alternative Accepted
                          </option>
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="file"
                          multiple
                          onChange={(e) =>
                            handleQuoteItemImages(index, e.target.files)
                          }
                        />

                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            marginTop: "5px",
                          }}
                        >
                          {item.images?.map((img, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={URL.createObjectURL(img)}
                              alt=""
                              style={{
                                width: "45px",
                                height: "45px",
                                objectFit: "cover",
                                marginRight: "4px",
                                marginBottom: "4px",
                                borderRadius: "4px",
                              }}
                            />
                          ))}
                        </div>
                      </td>
                      <td className="text-center">
                        {quoteItems.length > 1 && (
                          <Button
                            size="sm"
                            variant="danger"
                            type="button"
                            onClick={() => removeQuoteItem(index)}
                          >
                            X
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Button
              type="button"
              size="m"
              variant="secondary"
              onClick={addQuoteItem}
            >
              + Add Product
            </Button>

            <Button
              className="CTL_btn btn m-1"
              type="submit"
              disabled={uploading}
            >
              {uploading ? "Loading..." : "Create Quote"}
            </Button>

            <span className="text-success ms-2">
              {createQuoteResponseState.message}
            </span>

            <span className="text-danger ms-2">
              {createQuoteResponseState.error}
            </span>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AdminCreateQuotePageComponent;